import React, { useState, useEffect } from 'react';

interface BoardMember {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  termStartDate?: string;
  termEndDate?: string;
  notes?: string;
  displayOrder: number;
  isActive: boolean;
}

interface FormData {
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  termStartDate: string;
  termEndDate: string;
  notes: string;
  displayOrder: number;
}

export default function BoardMemberManagement() {
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<BoardMember | null>(null);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    position: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    termStartDate: '',
    termEndDate: '',
    notes: '',
    displayOrder: 0
  });

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  };

  useEffect(() => {
    fetchBoardMembers();
  }, []);

  const fetchBoardMembers = async () => {
    try {
      setLoading(true);
      const data = await authenticatedFetch('/api/boardmembers');
      if (data.success) {
        setBoardMembers(data.data);
      }
    } catch (error) {
      console.error('Error fetching board members:', error);
      alert('Error loading board members');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    // Define CSV headers
    const headers = [
      'First Name',
      'Last Name',
      'Position',
      'Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Zip Code',
      'Term Start Date',
      'Term End Date',
      'Display Order',
      'Notes',
      'Active'
    ];

    // Map board members to CSV rows
    const rows = boardMembers.map(member => [
      member.firstName,
      member.lastName,
      member.position,
      member.email || '',
      member.phone || '',
      member.address || '',
      member.city || '',
      member.state || '',
      member.zipCode || '',
      member.termStartDate || '',
      member.termEndDate || '',
      member.displayOrder.toString(),
      member.notes || '',
      member.isActive ? 'Yes' : 'No'
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const cellStr = String(cell).replace(/"/g, '""');
        return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
          ? `"${cellStr}"` 
          : cellStr;
      }).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `board_members_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'displayOrder' ? parseInt(value) || 0 : value
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      termStartDate: '',
      termEndDate: '',
      notes: '',
      displayOrder: 0
    });
    setEditingMember(null);
    setShowAddModal(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (member: BoardMember) => {
    setEditingMember(member);
    setFormData({
      firstName: member.firstName,
      lastName: member.lastName,
      position: member.position,
      email: member.email || '',
      phone: member.phone || '',
      address: member.address || '',
      city: member.city || '',
      state: member.state || '',
      zipCode: member.zipCode || '',
      termStartDate: member.termStartDate || '',
      termEndDate: member.termEndDate || '',
      notes: member.notes || '',
      displayOrder: member.displayOrder
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.position.trim()) {
      alert('Please fill in all required fields (First Name, Last Name, Position)');
      return;
    }

    try {
      if (editingMember) {
        // Update existing member
        const data = await authenticatedFetch(`/api/boardmembers/${editingMember.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });

        if (data.success) {
          alert('Board member updated successfully');
          fetchBoardMembers();
          resetForm();
        }
      } else {
        // Create new member
        const data = await authenticatedFetch('/api/boardmembers', {
          method: 'POST',
          body: JSON.stringify(formData)
        });

        if (data.success) {
          alert('Board member added successfully');
          fetchBoardMembers();
          resetForm();
        }
      }
    } catch (error) {
      console.error('Error saving board member:', error);
      alert('Error saving board member');
    }
  };

  const handleDelete = async (member: BoardMember) => {
    if (!window.confirm(`Are you sure you want to remove ${member.firstName} ${member.lastName} from the board?`)) {
      return;
    }

    try {
      const data = await authenticatedFetch(`/api/boardmembers/${member.id}`, {
        method: 'DELETE'
      });

      if (data.success) {
        alert('Board member removed successfully');
        fetchBoardMembers();
      }
    } catch (error) {
      console.error('Error deleting board member:', error);
      alert('Error deleting board member');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Board Member Management</h2>
        <div className="flex gap-3">
          <button
            onClick={exportToCSV}
            className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors whitespace-nowrap text-sm sm:text-base inline-flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to CSV
          </button>
          <button
            onClick={handleAdd}
            className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors whitespace-nowrap text-sm sm:text-base"
          >
            + Add Board Member
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
        </div>
      ) : boardMembers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No board members found</p>
          <button
            onClick={handleAdd}
            className="text-ggaa-green hover:underline"
          >
            Add your first board member
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View - Hidden on mobile */}
          <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Term
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {boardMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.displayOrder}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.position}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {member.email && <div>📧 {member.email}</div>}
                        {member.phone && <div>📱 {member.phone}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.termStartDate && (
                        <div>
                          {new Date(member.termStartDate).getFullYear()}
                          {member.termEndDate && ` - ${new Date(member.termEndDate).getFullYear()}`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(member)}
                        className="text-ggaa-green hover:text-ggaa-green/80 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(member)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on desktop */}
          <div className="lg:hidden space-y-4">
            {boardMembers.map((member) => (
              <div key={member.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                        {member.displayOrder}
                      </span>
                      <h3 className="text-base font-semibold text-gray-900">
                        {member.firstName} {member.lastName}
                      </h3>
                    </div>
                    <p className="text-sm text-ggaa-green font-medium">{member.position}</p>
                  </div>
                </div>

                {(member.email || member.phone) && (
                  <div className="mb-3 space-y-1">
                    {member.email && (
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <span>📧</span>
                        <a href={`mailto:${member.email}`} className="hover:text-ggaa-green break-all">
                          {member.email}
                        </a>
                      </div>
                    )}
                    {member.phone && (
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <span>📱</span>
                        <a href={`tel:${member.phone}`} className="hover:text-ggaa-green">
                          {member.phone}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {member.termStartDate && (
                  <div className="text-sm text-gray-500 mb-3">
                    <span className="font-medium">Term: </span>
                    {new Date(member.termStartDate).getFullYear()}
                    {member.termEndDate && ` - ${new Date(member.termEndDate).getFullYear()}`}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => handleEdit(member)}
                    className="flex-1 bg-ggaa-green text-white px-4 py-2 rounded-md hover:bg-ggaa-green/90 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member)}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingMember ? 'Edit Board Member' : 'Add Board Member'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Board Position <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    placeholder="e.g., President, Vice President, Treasurer, Secretary"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Term Start Date
                    </label>
                    <input
                      type="date"
                      name="termStartDate"
                      value={formData.termStartDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Term End Date
                    </label>
                    <input
                      type="date"
                      name="termEndDate"
                      value={formData.termEndDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-ggaa-green focus:border-ggaa-green"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-ggaa-green text-white rounded-md hover:bg-ggaa-green/90"
                  >
                    {editingMember ? 'Update' : 'Add'} Board Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 